import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import Papa from 'papaparse';
import Highlighter from 'react-highlight-words';
import SearchBar from './searchInCSV';
import { isAndroid, isIphoneSafari } from '../App';

const TextHighlighter = ({ searchTerm, value, matches, currentMatchIndex, colIndex, isHeader = false, rowIndex = -1 }) => {
  const currentMatch = matches[currentMatchIndex];
  
  let activeIndex = -1;
  
  if (currentMatch) {
    if (isHeader) {
      if (currentMatch.colIndex === colIndex && currentMatch.isHeader) {
        activeIndex = currentMatch.occurrenceIndex;
      }
    } else {
      if (currentMatch.rowIndex === rowIndex && 
          currentMatch.colIndex === colIndex && 
          !currentMatch.isHeader) {
        activeIndex = currentMatch.occurrenceIndex;
      }
    }
  }

  return (
    <Highlighter
      searchWords={[searchTerm]}
      autoEscape={true}
      textToHighlight={value.toString()}
      activeIndex={activeIndex}
      activeStyle={{
        backgroundColor: '#165bc3',
        color: 'white',
        fontWeight: 'bold',
        borderRadius: '2px',
        padding: '1px 2px'
      }}
      highlightStyle={{
        backgroundColor: 'rgba(255, 255, 0, 0.6)',
        color: 'inherit',
        fontWeight: 'normal',
      }}
      highlightClassName="fade-in"
    />
  )
}
const MemoizedTextHighlighter = React.memo(TextHighlighter);

const CsvViewer = ({ csvUrl, searchPluginInstance, isCsv = true }) => {
  const [csvData, setCsvData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [matches, setMatches] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [columns, setColumns] = useState([]);
  const [columnWidths, setColumnWidths] = useState({});
  const tableRef = useRef(null);
  const tableContainerRef = useRef(null);
  const searchBarRef = useRef(null);
  const isMobile = isAndroid() || isIphoneSafari();

  const allParsedData = useRef([]);
  const isDataFullyParsed = useRef(false);
  const pendingSearch = useRef(null);

  // NEW: Web-only timing improvements
  const highlightsRenderedRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const calculateColumnWidths = useCallback((data, headers) => {
    const MIN_WIDTH = 80;
    const MAX_WIDTH = 300;
    const CHAR_WIDTH = 8;
    const PADDING = 20;
  
    return headers.reduce((widths, header) => {
      const values = data.map(row => row[header]?.toString() || '');
      const headerLength = header.length;
      const maxValueLength = Math.max(...values.map(val => val.length), headerLength);
      
      const calculatedWidth = maxValueLength * CHAR_WIDTH + PADDING;
      widths[header] = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, calculatedWidth));
      
      return widths;
    }, {});
  }, []);
  
  const findAllOccurrences = (text, searchTerm) => {
    const occurrences = [];
    const lowerText = text.toLowerCase();
    const lowerSearch = searchTerm.toLowerCase();
    let currentIndex = lowerText.indexOf(lowerSearch);
  
    while (currentIndex !== -1) {
      occurrences.push({
        start: currentIndex,
        end: currentIndex + searchTerm.length
      });
      currentIndex = lowerText.indexOf(lowerSearch, currentIndex + 1);
    }
  
    return occurrences;
  };
  
  // NEW: Web-only highlight waiting logic
  const waitForHighlights = useCallback((cell, maxWaitTime = 2000) => {
    if (isMobile) {
      // Mobile: Skip complex timing, return immediately
      return Promise.resolve(true);
    }
    
    return new Promise((resolve) => {
      if (cell.querySelectorAll('mark').length > 0) {
        resolve(true);
        return;
      }
  
      const startTime = Date.now();
      let isResolved = false;
  
      const checkForHighlights = () => {
        if (isResolved) return;
  
        const hasHighlights = cell.querySelectorAll('mark').length > 0;
        
        if (hasHighlights) {
          isResolved = true;
          resolve(true);
          return;
        }
  
        // Check if timeout reached
        if (Date.now() - startTime > maxWaitTime) {
          isResolved = true;
          resolve(false);
          return;
        }
  
        requestAnimationFrame(checkForHighlights);
      };
  
      requestAnimationFrame(checkForHighlights);
    });
  }, [isMobile]);
// In your useEffect that exposes functions to window, add these 2 lines:

  // HYBRID: scrollToMatch with mobile-simple vs web-enhanced logic
  const scrollToMatch = useCallback(async (index) => {
    if (!tableRef.current || !tableContainerRef.current || matches.length === 0 || index < 0) {
      return;
    }

    const match = matches[index];
    if (!match) return;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // MOBILE: Use simple, immediate scrolling
    if (isMobile) {
      setTimeout(() => {
        let cell;
        if (match.isHeader) {
          cell = tableRef.current.querySelector(
            `thead tr th:nth-child(${match.colIndex + 1})`
          );
        } else {
          cell = tableRef.current.querySelector(
            `tbody tr:nth-child(${match.rowIndex + 1}) td:nth-child(${match.colIndex + 1})`
          );
        }

        if (cell && tableContainerRef.current) {
          const container = tableContainerRef.current;
          const cellAbsoluteTop = cell.offsetTop;
          const cellAbsoluteLeft = cell.offsetLeft;

          const targetScrollTop = cellAbsoluteTop - (container.clientHeight / 2) + (cell.offsetHeight / 2);
          const targetScrollLeft = cellAbsoluteLeft - (container.clientWidth / 2) + (cell.offsetWidth / 2);

          const maxScrollTop = tableRef.current.scrollHeight - container.clientHeight;
          const maxScrollLeft = tableRef.current.scrollWidth - container.clientWidth;

          const boundedScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));
          const boundedScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

          container.scrollTo({
            top: boundedScrollTop,
            left: boundedScrollLeft,
            behavior: 'smooth'
          });
        }
      }, 100);
      return;
    }

    // WEB: Use enhanced scrolling with highlight detection
    const attemptScroll = async (attemptCount = 0) => {
      const maxAttempts = 5;
      
      try {
        let cell;
        if (match.isHeader) {
          cell = tableRef.current.querySelector(
            `thead tr th:nth-child(${match.colIndex + 1})`
          );
        } else {
          cell = tableRef.current.querySelector(
            `tbody tr:nth-child(${match.rowIndex + 1}) td:nth-child(${match.colIndex + 1})`
          );
        }

        if (cell) {
          const highlightsFound = await waitForHighlights(cell);
          
          if (!highlightsFound && attemptCount < maxAttempts) {
            scrollTimeoutRef.current = setTimeout(() => {
              attemptScroll(attemptCount + 1);
            }, 100 * (attemptCount + 1));
            return;
          }

          const container = tableContainerRef.current;
          const cellAbsoluteTop = cell.offsetTop;
          const cellAbsoluteLeft = cell.offsetLeft;

          const targetScrollTop = cellAbsoluteTop - (container.clientHeight / 2) + (cell.offsetHeight / 2);
          const targetScrollLeft = cellAbsoluteLeft - (container.clientWidth / 2) + (cell.offsetWidth / 2);

          const maxScrollTop = tableRef.current.scrollHeight - container.clientHeight;
          const maxScrollLeft = tableRef.current.scrollWidth - container.clientWidth;

          const boundedScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));
          const boundedScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

          container.scrollTo({
            top: boundedScrollTop,
            left: boundedScrollLeft,
            behavior: 'smooth'
          });

          highlightsRenderedRef.current = true;
        }
      } catch (error) {
        console.error('Error during scroll attempt:', error);
      }
    };

    highlightsRenderedRef.current = false;
    attemptScroll();
  }, [matches, waitForHighlights, isMobile]);

  // HYBRID: searchKeyword with OLD mobile logic + NEW web features
  const searchKeyword = useCallback((keyword) => {
    const trimmed = keyword?.trim() ?? '';

    // If empty keyword, clear results - SAME FOR BOTH
    if (trimmed === '') {
      setSearchTerm('');
      setMatches([]);
      setCurrentMatchIndex(-1);
      if (isMobile) {
        // OLD MOBILE LOGIC: Immediate messaging
        window.ReactNativeWebView?.postMessage('searchResult: []');
      }
      pendingSearch.current = null;
      return;
    }

    setSearchTerm(trimmed);

    // If data isn't fully parsed yet, store the search for later - SAME FOR BOTH
    if (!isDataFullyParsed.current && allParsedData.current.length === 0) {
      pendingSearch.current = trimmed;
      if (isMobile) {
        // OLD MOBILE LOGIC: Immediate messaging
        window.ReactNativeWebView?.postMessage('searchResult: loading');
      }
      return;
    }

    // Use the most complete data available - SAME FOR BOTH
    const searchData = allParsedData.current.length > 0 ? allParsedData.current : csvData;

    if (searchData.length === 0 && columns.length === 0) {
      if (isMobile) {
        // OLD MOBILE LOGIC: Immediate messaging
        window.ReactNativeWebView?.postMessage('searchResult: 0');
      }
      return 0;
    }

    const lowerKeyword = trimmed.toLowerCase();
    const newMatches = [];

    // NEW: Enhanced search with header support and occurrence tracking
    columns.forEach((column, colIndex) => {
      if (column && column.toString().toLowerCase().includes(lowerKeyword)) {
        const occurrences = findAllOccurrences(column.toString(), trimmed);
        occurrences.forEach((occurrence, occurrenceIndex) => {
          newMatches.push({
            colIndex,
            isHeader: true,
            value: column.toString(),
            occurrenceIndex,
            start: occurrence.start,
            end: occurrence.end
          });
        });
      }
    });

    // NEW: Enhanced data search with occurrence tracking
    searchData.forEach((row, rowIndex) => {
      if (!row) return;

      Object.entries(row).forEach(([key, value], colIndex) => {
        const stringValue = value?.toString() || '';
        if (stringValue.toLowerCase().includes(lowerKeyword)) {
          const occurrences = findAllOccurrences(stringValue, trimmed);
          occurrences.forEach((occurrence, occurrenceIndex) => {
            newMatches.push({
              rowIndex,
              colIndex,
              isHeader: false,
              value: stringValue,
              occurrenceIndex,
              start: occurrence.start,
              end: occurrence.end
            });
          });
        }
      });
    });

    setMatches(newMatches);

    // AUTO-SELECT FIRST MATCH - SAME FOR BOTH
    const newCurrentIndex = newMatches.length > 0 ? 0 : -1;
    setCurrentMatchIndex(newCurrentIndex);

    // HYBRID: Different scrolling strategies
    if (newMatches.length > 0) {
      if (isMobile) {
        // OLD MOBILE LOGIC: Simple delayed scroll
        setTimeout(() => {
          scrollToMatch(newCurrentIndex);
        }, 150);
      } else {
        // NEW WEB LOGIC: Enhanced timing with multiple strategies
        const scrollToFirstMatch = () => {
          setTimeout(() => {
            scrollToMatch(newCurrentIndex);
          }, 200);
        };
        scrollToFirstMatch();
        requestAnimationFrame(() => {
          setTimeout(scrollToFirstMatch, 300);
        });
      }
    }

    // CRITICAL: OLD MOBILE MESSAGING LOGIC (preserved from working version)
    if (isMobile) {
      const resultCount = newMatches.length;
      const message = isCsv
        ? `searchResult: ${JSON.stringify(newMatches)}`
        : `searchResult: ${resultCount}`;
    
      // OLD WORKING MOBILE LOGIC: Immediate messaging with minimal delay
      requestAnimationFrame(() => {
        setTimeout(() => {
          window.ReactNativeWebView?.postMessage(message);
        }, 100);
      });
    }

    return newMatches.length;
  }, [csvData, columns, isMobile, scrollToMatch, isCsv]);

  const handleNextMatch = useCallback(() => {
    if (matches.length > 0) {
      const nextIndex = (currentMatchIndex + 1) % matches.length;
      setCurrentMatchIndex(nextIndex);
      
      if (isMobile) {
        // Mobile: Simple scroll
        scrollToMatch(nextIndex);
      } else {
        // Web: Delayed scroll for better timing
        setTimeout(() => {
          scrollToMatch(nextIndex);
        }, 50);
      }
    }
  }, [currentMatchIndex, matches, scrollToMatch, isMobile]);

  const handlePreviousMatch = useCallback(() => {
    if (matches.length > 0) {
      const prevIndex = (currentMatchIndex - 1 + matches.length) % matches.length;
      setCurrentMatchIndex(prevIndex);
      
      if (isMobile) {
        // Mobile: Simple scroll
        scrollToMatch(prevIndex);
      } else {
        // Web: Delayed scroll for better timing
        setTimeout(() => {
          scrollToMatch(prevIndex);
        }, 50);
      }
    }
  }, [currentMatchIndex, matches, scrollToMatch, isMobile]);

  // Rest of your data loading and other effects remain the same...
  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(csvUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((text) => {
        if (text) {
          papaparseCsv(text);
        } else {
          throw new Error('Empty response from server');
        }
      })
      .catch((error) => {
        setError(error);
        setLoading(false);
      });
  }, [csvUrl]);

  const papaparseCsv = useCallback((text) => {
    allParsedData.current = [];
    isDataFullyParsed.current = false;

    Papa.parse(text, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        // Load all data at once
        allParsedData.current = results.data || [];
        isDataFullyParsed.current = true;
        setCsvData([...allParsedData.current]);

        if (allParsedData.current.length > 0) {
          const headers = Object.keys(allParsedData.current[0]);
          setColumns(headers);
          setColumnWidths(calculateColumnWidths(allParsedData.current, headers));
        }

        if (pendingSearch.current) {
          searchKeyword(pendingSearch.current);
          pendingSearch.current = null;
        } else if (searchTerm) {
          searchKeyword(searchTerm);
        }

        setLoading(false);
      },
      error: function (error) {
        setError(error);
        setLoading(false);
      }
    });
  }, [searchTerm, searchKeyword, calculateColumnWidths]);

  // Add mobile event listener
  const handleMobileSearch = useCallback((event) => {
    try {
      let keyword;
      
      if (typeof event.data === 'string') {
        try {
          const data = JSON.parse(event.data);
          keyword = data.keyword;
        } catch (e) {
          if (event.data.includes('keyword')) {
            const match = event.data.match(/"keyword":"([^"]*)"/);
            keyword = match ? match[1] : event.data.replace('searchKeyword:', '').replace('keyword:', '').trim();
          } else {
            keyword = event.data;
          }
        }
      } else if (event.data && typeof event.data === 'object') {
        keyword = event.data.keyword;
      }
      
      if (keyword !== undefined && keyword !== null) {
        console.log('Mobile search received:', keyword);
        searchKeyword(keyword);
      }
    } catch (error) {
      console.error('Error handling mobile search:', error);
    }
  }, [searchKeyword]);
  useEffect(() => {
    // Expose functions to window for mobile (from older code)
    window.searchKeyword = (keyword) => {
      return searchKeyword(keyword);
    };
  
    window.clearSearch = () => {
      setSearchTerm('');
      setMatches([]);
      setCurrentMatchIndex(-1);
    };
  
    // ADD JUST THESE 2 LINES - that's all you need!
    window.focusNext = handleNextMatch;
    window.focusPrevious = handlePreviousMatch;
  
    if (isMobile) {
      window.addEventListener('message', handleMobileSearch);
      return () => {
        window.removeEventListener('message', handleMobileSearch);
      };
    }
  }, [isMobile, handleMobileSearch, searchKeyword, handleNextMatch, handlePreviousMatch]);
  useEffect(() => {
    // Expose functions to window for mobile (from older code)
    window.searchKeyword = (keyword) => {
      return searchKeyword(keyword);
    };

    window.clearSearch = () => {
      setSearchTerm('');
      setMatches([]);
      setCurrentMatchIndex(-1);
    };

    if (isMobile) {
      window.addEventListener('message', handleMobileSearch);
      return () => {
        window.removeEventListener('message', handleMobileSearch);
      };
    }
  }, [isMobile, handleMobileSearch, searchKeyword]);

  // Rest of your component (loading, error, return JSX) remains the same...
  if (loading) {
    return <div className="csv-loading"></div>;
  }

  if (error) {
    return (
      <div className="csv-error">
        <h3>Error Loading Data</h3>
        <p>{error.message}</p>
        <button onClick={() => window.location.reload()} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="csv-viewer-container">
      <div className="csv-header">
        {!isMobile && (
          <div className="search-container">
            <SearchBar
              searchPluginInstance={searchPluginInstance}
              showSearchBar={true}
              onSearch={searchKeyword}
              onNextMatch={handleNextMatch}
              onPreviousMatch={handlePreviousMatch}
              ref={searchBarRef}
            />
          </div>
        )}
        <div className="header-info">
          <div className="file-stats">
            {searchTerm && (
              <span className="search-results">
                {matches.length > 0 ? `${currentMatchIndex + 1} of ${matches.length}` : '0'} matches for "{searchTerm}"
              </span>
            )}
          </div>
        </div>
      </div>

      <div
        ref={tableContainerRef}
        className="table-container"
      >
        {csvData.length > 0 ? (
          <>
            <table ref={tableRef} className="csv-table">
              <thead>
                <tr>
                  {columns.map((key, colIndex) => (
                    <th
                      key={key}
                      style={{
                        width: columnWidths[key] || 'auto',
                        minWidth: '80px'
                      }}
                      title={key}
                    >
                      <div className="column-header">
                        <MemoizedTextHighlighter
                          colIndex={colIndex}
                          currentMatchIndex={currentMatchIndex}
                          matches={matches}
                          searchTerm={searchTerm}
                          value={key}
                          isHeader={true}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvData.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={rowIndex % 2 === 0 ? 'even-row' : 'odd-row'}
                  >
                    {columns.map((key, colIndex) => (
                      <td
                        key={colIndex}
                        style={{
                          width: columnWidths[key] || 'auto',
                          minWidth: '80px'
                        }}
                        className="data-cell"
                      >
                        <div className="cell-content">
                          <MemoizedTextHighlighter
                            colIndex={colIndex}
                            currentMatchIndex={currentMatchIndex}
                            matches={matches}
                            searchTerm={searchTerm}
                            value={row[key] || ''}
                            isHeader={false}
                            rowIndex={rowIndex}
                          />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <div className="no-data">
            <h3>No Data Available</h3>
            <p>The CSV file appears to be empty or couldn't be parsed.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CsvViewer;